import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { GreenManureApiService } from './green-manure-api.service';
import { CropFieldSnapshot, GreenManureAnswers, GreenManureContext } from './green-manure.model';

const newAnswers = (): GreenManureAnswers => ({
  nextMainCropId: null,
  isNonInversionTillage: false,
  nematodeConcerns: null,
  diseaseConcerns: null,
  managementSystem: null,
  priorities: null,
});

function sameList<T>(left: readonly T[] | null, right: readonly T[] | null): boolean {
  return left === right || (left !== null && right !== null &&
    left.length === right.length && left.every((value, index) => value === right[index]));
}

function sameAnswers(left: GreenManureAnswers, right: GreenManureAnswers): boolean {
  return left.nextMainCropId === right.nextMainCropId &&
    left.isNonInversionTillage === right.isNonInversionTillage &&
    sameList(left.nematodeConcerns, right.nematodeConcerns) &&
    sameList(left.diseaseConcerns, right.diseaseConcerns) &&
    left.managementSystem === right.managementSystem &&
    sameList(left.priorities, right.priorities);
}

/** Dialog-local draft and persistence; each save replaces the complete answer set. */
@Injectable()
export class GreenManureQuestionnaireService {
  private readonly api = inject(GreenManureApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly baseline = signal(newAnswers());
  private initialSnapshot?: CropFieldSnapshot;
  readonly answers = signal<GreenManureAnswers>(newAnswers());
  private answerField<K extends keyof GreenManureAnswers>(key: K) {
    const value = computed(() => this.answers()[key]);
    const set = (next: GreenManureAnswers[K]) => this.answers.update((answers) => ({ ...answers, [key]: next }));
    return Object.assign(value, {
      set,
      update: (updater: (current: GreenManureAnswers[K]) => GreenManureAnswers[K]) => set(updater(value())),
    });
  }
  readonly selectedMainCropId = this.answerField('nextMainCropId');
  readonly isNonInversionTillage = this.answerField('isNonInversionTillage');
  readonly selectedNematodes = this.answerField('nematodeConcerns');
  readonly selectedDiseases = this.answerField('diseaseConcerns');
  readonly selectedSystem = this.answerField('managementSystem');
  readonly priorities = this.answerField('priorities');
  readonly selectedPriorities = Object.assign(
    computed(() => this.priorities() ?? []),
    {
      set: (priorities: NonNullable<GreenManureAnswers['priorities']>) =>
        this.priorities.set(priorities.length === 0 && this.baseline().priorities === null ? null : priorities),
    },
  );
  readonly snapshot = signal<CropFieldSnapshot | null>(null);
  readonly loading = signal(true);
  readonly loadFailed = signal(false);
  readonly operation = signal<'save' | 'refresh' | null>(null);
  readonly error = signal<'save' | 'refresh' | null>(null);
  readonly hasUnsavedChanges = computed(() => !sameAnswers(this.answers(), this.baseline()));
  readonly ready = computed(() => !this.loading() && !this.loadFailed() && this.operation() === null);

  load(context: GreenManureContext): void {
    if (this.operation() !== null) return;
    this.loading.set(true);
    this.loadFailed.set(false);
    this.api
      .getQuestionnaire(context)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (questionnaire) => {
          const answers = questionnaire?.answers ?? newAnswers();
          this.baseline.set({ ...newAnswers(), ...answers });
          this.answers.set(answers);
          this.snapshot.set(questionnaire?.cropFieldSnapshot ?? null);
        },
        error: () => this.loadFailed.set(true),
      });
  }

  save(
    context: GreenManureContext,
    currentSnapshot: CropFieldSnapshot,
    onSaved?: () => void,
    onError?: () => void,
  ): void {
    if (!this.ready()) return;
    if (!this.hasUnsavedChanges()) {
      this.error.set(null);
      onSaved?.();
      return;
    }
    const answers = this.answers();
    // A failed response may still have persisted the first save. Reuse identical context
    // on retry rather than accidentally requesting an implicit snapshot refresh.
    if (this.snapshot() === null) this.initialSnapshot ??= currentSnapshot;
    const initialSnapshot = this.snapshot() === null ? this.initialSnapshot : undefined;
    this.operation.set('save');
    this.error.set(null);
    this.api
      .saveQuestionnaire(context, answers, initialSnapshot)
      .pipe(
        finalize(() => this.operation.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.baseline.set(answers);
          if (initialSnapshot) this.snapshot.set(initialSnapshot);
          onSaved?.();
        },
        error: () => {
          this.error.set('save');
          onError?.();
        },
      });
  }

  refreshSnapshot(context: GreenManureContext, snapshot: CropFieldSnapshot, onRefreshed?: () => void): void {
    if (!this.ready() || this.snapshot() === null) return;
    this.operation.set('refresh');
    this.error.set(null);
    this.api
      .refreshSnapshot(context, snapshot)
      .pipe(
        finalize(() => this.operation.set(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.snapshot.set(snapshot);
          onRefreshed?.();
        },
        error: () => this.error.set('refresh'),
      });
  }
}
